import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '@/api/api';
import { Clock, Globe, Save, RefreshCw, Calendar } from 'lucide-react';

const TimezoneSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [settings, setSettings] = useState({
    timezone: 'Asia/Kolkata',
    week_start_day: 'Monday',
    working_days_per_month: 26,
    academic_year_start_month: 'July',
  });

  const timezones = [
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', offset: 'UTC+5:30' },
    { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (BST)', offset: 'UTC+6:00' },
    { value: 'Asia/Kathmandu', label: 'Nepal Time (NPT)', offset: 'UTC+5:45' },
    { value: 'Asia/Colombo', label: 'Sri Lanka Time (SLST)', offset: 'UTC+5:30' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)', offset: 'UTC+4:00' },
    { value: 'Asia/Singapore', label: 'Singapore Time (SGT)', offset: 'UTC+8:00' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)', offset: 'UTC+9:00' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)', offset: 'UTC+0:00' },
    { value: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5:00' },
  ];

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchSettings();
    // Update current time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/master-settings/timezone-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
        // Save to localStorage for global access
        localStorage.setItem('timezoneSettings', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.post('/master-settings/timezone-settings', settings);
      if (response.data?.success) {
        // Update localStorage when settings are saved
        localStorage.setItem('timezoneSettings', JSON.stringify(settings));
        toast.success(`Settings saved! Working days: ${settings.working_days_per_month}`);
      } else {
        toast.error('Save failed - please try again');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Get time in selected timezone
  const getTimeInTimezone = () => {
    try {
      return currentTime.toLocaleString('en-IN', { 
        timeZone: settings.timezone,
        dateStyle: 'full',
        timeStyle: 'medium'
      });
    } catch {
      return currentTime.toLocaleString('en-IN');
    }
  };

  const getSelectedTimezoneInfo = () => {
    return timezones.find(tz => tz.value === settings.timezone);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="timezone-settings">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Globe className="w-7 h-7 text-blue-600" />
            Timezone Settings
          </h1>
          <p className="text-gray-600 mt-1">Configure timezone and working day settings for your institution</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Live Clock Preview */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Current Date & Time ({getSelectedTimezoneInfo()?.label})</p>
              <p className="text-3xl font-bold">{getTimeInTimezone()}</p>
              <p className="text-blue-200 text-sm mt-2">{getSelectedTimezoneInfo()?.offset}</p>
            </div>
            <Clock className="w-16 h-16 text-blue-200 opacity-50" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Timezone Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5 text-blue-600" />
              System Timezone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {timezones.map((tz) => (
                <label 
                  key={tz.value} 
                  className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    settings.timezone === tz.value 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="timezone"
                    value={tz.value}
                    checked={settings.timezone === tz.value}
                    onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="ml-3 flex-1">
                    <span className="font-medium text-gray-800">{tz.label}</span>
                  </span>
                  <span className="text-gray-500 text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                    {tz.offset}
                  </span>
                </label>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Important:</strong> Changing timezone will affect all date and time displays across the application including attendance, reports, and schedules.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Working Days Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-green-600" />
              Working Days Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Week Start Day</label>
              <select
                value={settings.week_start_day}
                onChange={(e) => setSettings({...settings, week_start_day: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {weekDays.map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">First day of the week in calendars and reports</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Days per Month
                <span className="text-xs text-blue-500 ml-2">(Used for salary calculation)</span>
              </label>
              <input
                type="number"
                min="20"
                max="31"
                value={settings.working_days_per_month}
                onChange={(e) => setSettings({...settings, working_days_per_month: parseInt(e.target.value) || 26})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Standard: 26 days (excluding Sundays). Used for pro-rata salary calculation.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year Start Month</label>
              <select
                value={settings.academic_year_start_month}
                onChange={(e) => setSettings({...settings, academic_year_start_month: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {months.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Usually July in India, September in other countries</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-green-800">
                <strong>ℹ️ Pro-rata Salary Example:</strong><br/>
                Monthly Salary: ₹30,000<br/>
                Working Days: {settings.working_days_per_month}<br/>
                Per Day: ₹{Math.round(30000 / settings.working_days_per_month).toLocaleString('en-IN')}<br/>
                If present 20 days: ₹{Math.round((30000 / settings.working_days_per_month) * 20).toLocaleString('en-IN')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-800 mb-2">📋 Current Settings Summary:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Timezone:</span>
              <p className="font-medium">{getSelectedTimezoneInfo()?.label}</p>
            </div>
            <div>
              <span className="text-gray-500">Week Starts:</span>
              <p className="font-medium">{settings.week_start_day}</p>
            </div>
            <div>
              <span className="text-gray-500">Working Days/Month:</span>
              <p className="font-medium">{settings.working_days_per_month} days</p>
            </div>
            <div>
              <span className="text-gray-500">Academic Year Starts:</span>
              <p className="font-medium">{settings.academic_year_start_month}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimezoneSettings;
